import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faCheck,
  faXmark,
  faForward,
  faFilter,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest } from "services/api/apiService";

const PLATFORMS = [
  { value: "all", label: "All Platforms" },
  { value: "meta", label: "Meta" },
  { value: "tiktok", label: "TikTok" },
  { value: "google", label: "Google" },
  { value: "snapchat", label: "Snapchat" },
  { value: "linkedin", label: "LinkedIn" },
];

const RESULTS = [
  { value: "all", label: "All Results" },
  { value: "success", label: "Success" },
  { value: "skipped", label: "Skipped" },
  { value: "failed", label: "Failed" },
];

const PLATFORM_COLORS = {
  meta: "bg-blue-100 text-blue-700",
  tiktok: "bg-pink-100 text-pink-700",
  google: "bg-yellow-100 text-yellow-700",
  snapchat: "bg-yellow-50 text-yellow-600",
  linkedin: "bg-sky-100 text-sky-700",
};

const RESULT_STYLES = {
  success: { bg: "bg-emerald-50", text: "text-emerald-700", icon: faCheck, label: "Success" },
  skipped: { bg: "bg-gray-50", text: "text-gray-500", icon: faForward, label: "Skipped" },
  failed: { bg: "bg-red-50", text: "text-red-600", icon: faXmark, label: "Failed" },
};

const operatorLabel = (op) => {
  const map = { ">": ">", "<": "<", ">=": "≥", "<=": "≤", "=": "=", gt: ">", gte: "≥", lt: "<", lte: "≤" };
  return map[op] || op;
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdsHistory = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ platform: "all", result: "all", ad_account_id: "" });
  const [accounts, setAccounts] = useState([]);
  const LIMIT = 10;

  // Fetch ad accounts for filter dropdown
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await getRequest("/api/ad-accounts");
        setAccounts(data);
      } catch {
        /* silent */
      }
    };
    loadAccounts();
  }, []);

  // Refetch accounts and reset ad_account when platform changes
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const q = filters.platform !== "all" ? `?platform=${filters.platform}` : "";
        const data = await getRequest(`/api/ad-accounts${q}`);
        setAccounts(data);
      } catch {
        /* silent */
      }
    };
    loadAccounts();
    setFilters((prev) => ({ ...prev, ad_account_id: "" }));
  }, [filters.platform]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const currentOffset = reset ? 0 : offset;
        const params = new URLSearchParams();
        params.set("limit", LIMIT);
        params.set("offset", currentOffset);
        if (filters.platform !== "all") params.set("platform", filters.platform);
        if (filters.result !== "all") params.set("result", filters.result);
        if (filters.ad_account_id) params.set("ad_account_id", filters.ad_account_id);

        const res = await getRequest(`/api/ads-history?${params.toString()}`);
        if (reset) {
          setLogs(res.data);
          setOffset(res.data.length);
        } else {
          setLogs((prev) => [...prev, ...res.data]);
          setOffset((prev) => prev + res.data.length);
        }
        setTotal(res.total);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    },
    [offset, filters] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    fetchLogs(true);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setOffset(0);
  };

  const hasMore = offset < total;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faClockRotateLeft} className="text-[#221C51] text-xl" />
          <div>
            <h1 className="text-xl font-bold text-[#221C51]">Rule Activity</h1>
            <p className="text-xs text-gray-500">
              Execution logs — every rule evaluation and action taken by the engine.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <FontAwesomeIcon icon={faFilter} />
          <span className="font-semibold">Filters</span>
        </div>

        {/* Platform */}
        <div className="relative">
          <select
            value={filters.platform}
            onChange={(e) => handleFilterChange("platform", e.target.value)}
            className="appearance-none border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
          >
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"
          />
        </div>

        {/* Ad Account */}
        <div className="relative">
          <select
            value={filters.ad_account_id}
            onChange={(e) => handleFilterChange("ad_account_id", e.target.value)}
            className="appearance-none border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
          >
            <option value="">All Accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.account_name}</option>
            ))}
          </select>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"
          />
        </div>

        {/* Result */}
        <div className="relative">
          <select
            value={filters.result}
            onChange={(e) => handleFilterChange("result", e.target.value)}
            className="appearance-none border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
          >
            {RESULTS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"
          />
        </div>
      </div>

      {/* Body */}
      {loading && logs.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          Loading history…
        </div>
      ) : logs.length === 0 ? (
        <HistoryEmptyState />
      ) : (
        <div className="space-y-2 overflow-y-auto">
          {logs.map((log) => (
            <HistoryRow key={log.id} log={log} />
          ))}

          {hasMore && (
            <div className="flex justify-center pt-4 pb-2">
              <button
                onClick={() => fetchLogs(false)}
                disabled={loading}
                className="px-5 py-2 text-sm rounded-lg border border-[#221C51]/20 text-[#221C51] hover:bg-[#221C51]/5 transition disabled:opacity-50"
              >
                {loading ? "Loading…" : "Load More"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const HistoryRow = ({ log }) => {
  const rs = RESULT_STYLES[log.result] || RESULT_STYLES.skipped;

  return (
    <div className="flex items-center justify-between border border-gray-100 rounded-xl px-5 py-3.5 bg-white hover:border-[#221C51]/10 transition">
      <div className="flex items-center space-x-4 min-w-0 flex-1">
        {/* Platform badge */}
        <div
          className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
            PLATFORM_COLORS[log.platform] || "bg-gray-100 text-gray-700"
          }`}
        >
          {PLATFORMS.find((p) => p.value === log.platform)?.label || log.platform}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[#221C51] text-sm truncate">{log.rule_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {log.metric.toUpperCase()} {operatorLabel(log.operator)} {log.threshold}€
            {" → "}
            <span className="font-medium">actual: {log.actual_value}€</span>
            {log.campaign_name && (
              <span className="text-gray-400"> · {log.campaign_name}</span>
            )}
          </p>
          {log.ad_account_name && (
            <p className="text-[10px] text-gray-400 mt-0.5">{log.ad_account_name}</p>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-4 ml-4 shrink-0">
        {/* Action */}
        <span className="text-xs font-medium text-[#221C51] bg-[#221C51]/5 px-2.5 py-1 rounded-full capitalize">
          {log.action_taken}
        </span>

        {/* Result badge */}
        <span
          className={`inline-flex items-center space-x-1 text-xs font-semibold px-2.5 py-1 rounded-full ${rs.bg} ${rs.text}`}
        >
          <FontAwesomeIcon icon={rs.icon} className="text-[10px]" />
          <span>{rs.label}</span>
        </span>

        {/* Date */}
        <span className="text-xs text-gray-400 w-36 text-right">{formatDate(log.created_at)}</span>
      </div>
    </div>
  );
};

const HistoryEmptyState = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center py-20">
    <FontAwesomeIcon icon={faClockRotateLeft} className="text-5xl text-gray-200 mb-4" />
    <p className="text-gray-500 font-medium mb-1">No history yet</p>
    <p className="text-gray-400 text-sm max-w-xs">
      Rule execution logs will appear here once the engine starts evaluating your active rules.
    </p>
  </div>
);

export default AdsHistory;
